import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import { ResumeEducationItem } from '../../../types';
import { Palette, spacing, useThemePalette } from '../../../theme';
import RepeatableItemCard from '../RepeatableItemCard';
import DateField from '../DateField';

interface Props {
  value: ResumeEducationItem[];
  onChange: (next: ResumeEducationItem[]) => void;
}

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const blankItem = (): ResumeEducationItem => ({
  id: makeId(),
  institution: '',
  degree: '',
  field: '',
  startDate: '',
  endDate: '',
  description: '',
});

const EducationForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const update = (id: string, patch: Partial<ResumeEducationItem>) =>
    onChange(value.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const remove = (id: string) => onChange(value.filter((it) => it.id !== id));

  return (
    <View style={styles.container}>
      {value.map((item, idx) => (
        <RepeatableItemCard
          key={item.id}
          title={
            item.degree ||
            item.institution ||
            `${t('resume.sections.education')} ${idx + 1}`
          }
          onRemove={() => remove(item.id)}
        >
          <TextInput
            mode="outlined"
            label={t('resume.fields.institution')}
            value={item.institution}
            onChangeText={(v) => update(item.id, { institution: v })}
            dense
          />
          <TextInput
            mode="outlined"
            label={t('resume.fields.degree')}
            value={item.degree}
            onChangeText={(v) => update(item.id, { degree: v })}
            dense
          />
          <TextInput
            mode="outlined"
            label={t('resume.fields.field')}
            value={item.field}
            onChangeText={(v) => update(item.id, { field: v })}
            dense
          />
          <View style={styles.row}>
            <View style={styles.col}>
              <DateField
                label={t('resume.fields.startDate')}
                value={item.startDate}
                onChange={(v) => update(item.id, { startDate: v })}
              />
            </View>
            <View style={styles.col}>
              <DateField
                label={t('resume.fields.endDate')}
                value={item.endDate}
                onChange={(v) => update(item.id, { endDate: v })}
              />
            </View>
          </View>
          <TextInput
            mode="outlined"
            label={t('resume.fields.description')}
            value={item.description}
            onChangeText={(v) => update(item.id, { description: v })}
            multiline
            numberOfLines={3}
            style={styles.multiline}
          />
        </RepeatableItemCard>
      ))}
      <Button
        mode="outlined"
        icon="plus"
        onPress={() => onChange([...value, blankItem()])}
        textColor={palette.textPrimary}
        style={styles.addBtn}
      >
        {t('resume.buttons.addEducation')}
      </Button>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.sm },
    row: { flexDirection: 'row', gap: spacing.sm },
    col: { flex: 1 },
    multiline: { minHeight: 80 },
    addBtn: { borderRadius: 999, borderColor: p.border, marginTop: spacing.xs },
  });

export default EducationForm;
